/**
 * Блок 2 · Ф8 «Права доступа» · В-О1б — договор маршрутов выдачи прав.
 *
 *   GET /users/:id/rights          — карточка: восемь строк по одному человеку;
 *   PUT /users/:id/rights/:right   — добавить одно право либо сбросить строку;
 *   GET /user-rights               — сводный список: у кого что добавлено.
 *
 * Все три — только штатному супервайзеру (Р-186; умолчание 4 Р-211: должность
 * `CEO` того же уровня не проходит).
 *
 * ⚠️ Решение владельца 11.10.2026 (Ф8 · В-У): запрет (`DENY`) сервис не ставит
 * НИКОМУ — 422. Схема тела `DENY` по-прежнему допускает: значение перечисления
 * осталось в базе и в зеркале (иначе миграция и гейт R7), отказывает правило
 * (`isEffectApplicable` в `rights.ts`). Поля ответов про запрет —
 * `inheritedDenyFrom`, `inheritedDenies`, `assistants`, состояние `DENIED` —
 * оставлены: по ним экран показывает строку запрета, лежащую в базе с прежних
 * времён либо записанную мимо сервиса. Она действует, пока её не сбросят.
 *
 * ─── Что здесь НЕ решается ──────────────────────────────────────────────────
 *
 * Правило «откуда у человека право» живёт в `rights.ts` (`resolveRightState`,
 * `collectActorRights`) и здесь не повторяется: ответы несут уже посчитанное
 * состояние, а экран (волна В-Э) его показывает и сам ничего не вычисляет.
 *
 * ⚠️ `byRole` — описание ДОЛЖНОСТИ для экрана («по должности право есть»), а
 * не ответ «можно ли»: руководитель отдела при `byRole = false` свой отдел
 * по-прежнему видит. Решать доступ по этому полю нельзя — договор чтения
 * состояния записан в шапке `rights.ts`.
 */
import { z } from 'zod';
import { AccessLevel, RoleSlug, UserStatus } from './enums';
import { ruEnum } from './lib/zod-ru';
import { RightEffect, RightState, UserRight } from './rights';

// ─── Запрос ──────────────────────────────────────────────────────────────────

/** Сегмент пути `:right`. Разбирается по списку значений, а не по форме. */
export const UserRightParamSchema = z.nativeEnum(UserRight, ruEnum('Неизвестное право'));

/**
 * Тело `PUT /users/:id/rights/:right`.
 *
 * `version` — версия строки, которую видел вызывающий. У права, по которому
 * строки ещё нет, версии нет — поле не присылается. Несовпадение в любую
 * сторону (строка есть, а версии нет; версия есть, а строки нет; версия
 * устарела) — 409 `VERSION_CONFLICT`: карточку надо перечитать.
 *
 * `.strict()`: получатель и право приходят из пути, выдавший — из токена;
 * подложить их телом нельзя.
 *
 * ⚠️ `effect: DENY` схему проходит и получает 422 от правила — см. шапку файла.
 * Сужать перечисление здесь нельзя: это зеркало Prisma-enum (гейт R7).
 */
export const SetUserRightRequestSchema = z
  .object({
    effect: z.nativeEnum(RightEffect, ruEnum('Выберите состояние права')),
    version: z.number().int().nonnegative().optional(),
  })
  .strict();
export type SetUserRightRequest = z.infer<typeof SetUserRightRequestSchema>;

// ─── Ответы ──────────────────────────────────────────────────────────────────

/**
 * Почему строка с эффектом не действует.
 *
 *   ROLE_CHANGED   — снимок должности в строке не равен текущей должности
 *                    человека: должность сменили мимо сброса (Р-198, страховка);
 *   LEVEL_MISMATCH — строка на человека этого уровня не действует: `DENY`
 *                    действует только на администратора, `ALLOW` — только на
 *                    руководителя отдела и только по добавляемому праву (решение
 *                    плана П2, Р-217 б) — правило механизма `effectActsOnLevel`;
 *   NOT_ACTIVE     — человек не действующий (приглашён либо в архиве).
 *
 * Причина одна — первая по этому порядку: снимок проверяется раньше уровня,
 * как и в `collectActorRights` → `resolveRightState`.
 */
export const RightInertReason = {
  ROLE_CHANGED: 'ROLE_CHANGED',
  LEVEL_MISMATCH: 'LEVEL_MISMATCH',
  NOT_ACTIVE: 'NOT_ACTIVE',
} as const;
export type RightInertReason = (typeof RightInertReason)[keyof typeof RightInertReason];

const PersonRefSchema = z.object({
  id: z.string().uuid(),
  fullName: z.string(),
});
export type RightsPersonRef = z.infer<typeof PersonRefSchema>;

const RightsUserSchema = z.object({
  id: z.string().uuid(),
  fullName: z.string(),
  roleSlug: z.nativeEnum(RoleSlug),
  roleName: z.string(),
  accessLevel: z.nativeEnum(AccessLevel),
  status: z.nativeEnum(UserStatus),
  /**
   * Входит ли человек СЕЙЧАС в круг получателей прав: действующий руководитель
   * отдела либо администратор (Р-191; умолчание 3 Р-211). ⚠️ «Входит в круг» —
   * не «ему можно что-то поставить»: с 11.10.2026 администратору не ставится
   * ничего, руководителю — только `ALLOW`; что именно — `isEffectApplicable`.
   * Сброс (`INHERIT`) от этого признака не зависит — решение плана П4.
   */
  eligible: z.boolean(),
});
export type RightsUser = z.infer<typeof RightsUserSchema>;

/** Одна строка карточки — одно из восьми прав у одного человека. */
export const UserRightLineSchema = z.object({
  right: z.nativeEnum(UserRight),
  /** Даёт ли право должность. Для экрана; доступ по этому полю не решается. */
  byRole: z.boolean(),
  /** Что стоит в строке. `INHERIT` — и когда строки нет, и когда она сброшена. */
  effect: z.nativeEnum(RightEffect),
  /** Действует ли строка. У `INHERIT` — всегда `false`. */
  effective: z.boolean(),
  /** Почему не действует; `null` у действующей строки и у `INHERIT`. */
  inertReason: z.nativeEnum(RightInertReason).nullable(),
  /** ГИП, чей запрет наследуется (Р-197); `null`, если право не унаследовано закрытым. */
  inheritedDenyFrom: PersonRefSchema.nullable(),
  /**
   * Право «вести» не действует из-за парного «видеть» (Р-195): у руководителя
   * «видеть» не добавлено, у администратора — отнято.
   */
  viewMissing: z.boolean(),
  /** Итог ступени 1 — читается по договору шапки `rights.ts`. */
  state: z.nativeEnum(RightState),
  /** Кто менял строку последним; `null` — строки нет либо менявший не человек (посев). */
  changedBy: PersonRefSchema.nullable(),
  changedAt: z.string().datetime({ offset: true }).nullable(),
  /** Версия строки для следующего `PUT`; `null` — строки нет. */
  version: z.number().int().nonnegative().nullable(),
});
export type UserRightLine = z.infer<typeof UserRightLineSchema>;

/** `GET /users/:id/rights` и ответ `PUT /users/:id/rights/:right`. */
export const UserRightsResponseSchema = z.object({
  user: RightsUserSchema,
  /** ГИП, чьим помощником человек является; иначе `null`. */
  gip: PersonRefSchema.nullable(),
  /** Ровно восемь строк, в порядке `USER_RIGHTS`. */
  rights: z.array(UserRightLineSchema),
  /**
   * ДЕЙСТВУЮЩИЕ помощники человека (он их ГИП), по алфавиту; у остальных —
   * пусто. Запрет, лежащий у ГИПа, наследуют именно они (Р-197). ⚠️ После
   * 11.10.2026 экран этот перечень НЕ ЧИТАЕТ (он называл помощников перед
   * «Отнять» и «Вернуть», а этих действий больше нет) — поле осталось в ответе
   * как часть механизма запрета и уйдёт вместе с ним.
   */
  assistants: z.array(PersonRefSchema),
});
export type UserRightsResponse = z.infer<typeof UserRightsResponseSchema>;

/**
 * Строка сводного списка: право, по которому у человека стоит не `INHERIT`.
 * Штатно это `ALLOW` руководителя отдела; `DENY` — только лежащая строка
 * прежних времён (11.10.2026).
 */
export const UserRightOverrideSummarySchema = z.object({
  right: z.nativeEnum(UserRight),
  effect: z.nativeEnum(RightEffect),
  effective: z.boolean(),
  inertReason: z.nativeEnum(RightInertReason).nullable(),
  /**
   * «Вести» не действует из-за парного «видеть» (Р-195) — тот же признак, что
   * в строке карточки. Без него руководитель с «вести» без «видеть» выглядел
   * бы в списке как получивший право (Ф8 · В-Э, фикс-пак 1).
   */
  viewMissing: z.boolean(),
  changedAt: z.string().datetime({ offset: true }),
});
export type UserRightOverrideSummary = z.infer<typeof UserRightOverrideSummarySchema>;

/**
 * `GET /user-rights` — все, у кого есть строка с эффектом, ВКЛЮЧАЯ тех, у кого
 * она не действует (иначе залипшую строку не найти), плюс помощники с
 * унаследованным от ГИПа запретом (после 11.10.2026 — только от лежащей строки
 * `DENY`; штатно таких нет). Человек только со сброшенными строками в
 * список не попадает (Р-207: «узнаёт из сводного списка — человека в нём нет»).
 */
export const UserRightsListResponseSchema = z.object({
  items: z.array(
    z.object({
      user: RightsUserSchema,
      gip: PersonRefSchema.nullable(),
      overrides: z.array(UserRightOverrideSummarySchema),
      /** Права, закрытые действующим запретом ГИПа, в порядке `USER_RIGHTS`. */
      inheritedDenies: z.array(z.nativeEnum(UserRight)),
    }),
  ),
});
export type UserRightsListResponse = z.infer<typeof UserRightsListResponseSchema>;
